/**
 * Hiring-demand board — one unit and everything under it.
 *
 * A unit can hold roles two ways: inside a team, or directly on the unit. Both
 * exist because the org chart is genuinely uneven — Operations units have teams
 * and chapters, while Legal and Revenue Operations have none, and forcing a
 * one-team-per-unit placeholder onto those would be inventing structure.
 */
import type {
  BoardRole,
  DemandMonth,
  DraftInput,
  HireReason,
  RosterMember,
} from '../../../types/taDemand';
import { isRealUnit, rolesOfUnit, teamChoices, type Track, type UnitNode } from './demandModel';
import { GroupRow } from './DemandChrome';
import { AddTeam, UnitSubmit } from './DemandUnitControls';
import { RoleRow } from './DemandRows';
import { AddRoleRow, ComposerRow } from './DemandComposer';
import { OwnerTag, zoneClass } from './demandUi';
import { indentPx } from './demandGrid';
import { TeamRoster } from './TeamRoster';
import { CancelledRoles } from './CancelledRoles';
import type { TeamMoveApi } from './TeamMove';
import { MoveTo, type UnitChoice } from './Restructure';
import type { Placement, PlacementKind } from '../../../types/taDemand';

export function UnitBlock({
  unit,
  tracks,
  months,
  currentMonth,
  canApprove,
  isOpen,
  toggle,
  composing,
  setComposing,
  busy,
  onPatch,
  onPlace,
  onApprove,
  onDelete,
  onAddTeam,
  backfills,
  onRequestBackfill,
  composeError,
  unitMeta,
  onSubmitUnit,
  deptName,
  teamMove,
  restructuring,
  unitChoices,
  onRestructure,
}: {
  unit: UnitNode;
  tracks: Track[];
  months: DemandMonth[];
  currentMonth: string;
  canApprove: boolean;
  isOpen: (k: string, d?: boolean) => boolean;
  toggle: (k: string, d?: boolean) => void;
  composing: string | null;
  setComposing: (k: string | null) => void;
  busy: Record<string, boolean>;
  onPatch: (r: BoardRole, p: Partial<DraftInput>) => void;
  onPlace: (
    unitName: string,
    team: string,
    month: string,
    f: { title: string; level: string; reason: HireReason; count: number; fte: number },
  ) => void;
  onApprove: (r: BoardRole) => void;
  onDelete: (r: BoardRole) => void;
  onAddTeam: (name: string) => void;
  backfills: Map<string, string>;
  onRequestBackfill: (unitName: string, member: RosterMember) => void;
  composeError: string | null;
  unitMeta: Record<string, { submittedBy?: string; submittedAt?: string }>;
  onSubmitUnit: (unitName: string) => void;
  /** The department this unit belongs to — a restructure has to name both. */
  deptName: string;
  /** Drag-and-drop and the picker for putting a role in the right team. */
  teamMove: TeamMoveApi;
  /** Restructure mode: reveal the controls that move a team or a person. */
  restructuring: boolean;
  unitChoices: UnitChoice[];
  onRestructure: (
    body: {
      kind: PlacementKind;
      id?: string;
      department?: string;
      unit?: string;
      team?: string;
      section?: string;
    },
    to: Placement,
  ) => void;
}) {
  const uOpen = isOpen(unit.key);
  const anyBusy = Object.values(busy).some(Boolean);
  // A role can only be moved between the teams of its OWN unit: which unit it
  // sits in is the HDB's `Unit` column, and moving it here would silently
  // disagree with Airtable. The "unit not recognised" bucket has no teams and so
  // offers no move at all, which is right — the fix there is the unit name.
  const teams = teamChoices(unit);
  const movable = teams.length > 0;

  const roleRows = (roles: BoardRole[], depth: number) =>
    roles.map((role) => (
      <div key={role.id} className="group">
        <RoleRow
          role={role}
          tracks={tracks}
          months={months}
          currentMonth={currentMonth}
          depth={depth}
          canApprove={canApprove}
          busy={!!busy[role.id]}
          onPatch={(p) => onPatch(role, p)}
          onMove={(m) => onPatch(role, { month: m })}
          onApprove={() => onApprove(role)}
          onDelete={() => onDelete(role)}
          teams={teams}
          onMoveTeam={movable ? (t) => teamMove.move(role, t) : undefined}
          dragProps={movable ? teamMove.dragProps(role, unit.key) : undefined}
          dragging={teamMove.draggingId === role.id}
        />
      </div>
    ));

  return (
    <>
      <GroupRow
        tracks={tracks}
        months={months}
        currentMonth={currentMonth}
        depth={1}
        onClick={() => toggle(unit.key)}
        open={uOpen}
        tone="unit"
        roles={rolesOfUnit(unit)}
        label={
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="truncate text-[13px] font-semibold text-gray-900">{unit.name}</span>
            <OwnerTag owner={unit.owner} />
          </span>
        }
        actions={
          // Everything you do to a WHOLE unit lives on the unit's own row. Both
          // used to be elsewhere — submit hidden until hover, add-team as a row
          // at the very bottom of the unit's contents — so the two actions a
          // lead comes here for were the two hardest to find.
          isRealUnit(unit) && (
            <>
              {/*
                Only while the unit is OPEN. Adding a team you cannot see the
                existing teams of is guesswork — the answer to "do we already
                have one of these" is the rows underneath, and they are not on
                screen when the unit is shut. Submit stays either way: signing
                off does not need the detail in front of you.
              */}
              {uOpen && <AddTeam unitName={unit.name} onAdd={onAddTeam} />}
              <UnitSubmit
                submitted={unitMeta?.[unit.key]}
                busy={!!busy[`unit-submit-${unit.key}`]}
                onSubmit={() => onSubmitUnit(unit.name)}
              />
            </>
          )
        }
      />
      {uOpen && (
        <>
          {/* Same rule one level up: the unit's own seats sit directly under the
              unit, before its teams. For a unit whose only team is itself
              (`collapseSoleTeam`) this IS the team list. */}
          <TeamRoster
            members={unit.members}
            tracks={tracks}
            depth={2}
            label={unit.teams.length ? 'Current team · not in a team' : 'Current team'}
            busy={busy}
            backfills={backfills}
            onRequestBackfill={(m) => onRequestBackfill(unit.name, m)}
            restructuring={restructuring}
            unitChoices={unitChoices}
            deptName={deptName}
            unitName={unit.name}
            onRestructure={onRestructure}
          />

          {unit.teams.map((team) => {
            // Everything starts collapsed (Theresa): a department has ten units
            // and a unit has a dozen families, so anything else opens onto a
            // wall of rows.
            const tOpen = isOpen(team.key);
            return (
              <div key={team.key}>
                <GroupRow
                  tracks={tracks}
                  months={months}
                  currentMonth={currentMonth}
                  depth={2}
                  onClick={() => toggle(team.key)}
                  open={tOpen}
                  tone="team"
                  roles={team.roles}
                  drop={teamMove.dropProps(unit.key, team.name)}
                  actions={
                    // Only in restructure mode. A control that moves a whole
                    // team, sitting permanently on every team header, would be
                    // the most dangerous thing on the board.
                    restructuring && (
                      <MoveTo
                        what={team.name}
                        units={unitChoices}
                        teamsOnly
                        current={{ department: deptName, unit: unit.name }}
                        busy={!!busy[`place-team-${deptName}|${unit.name}|${team.name}`]}
                        onMove={(to) =>
                          onRestructure(
                            {
                              kind: 'team',
                              department: deptName,
                              unit: unit.name,
                              team: team.name,
                              section: deptName,
                            },
                            to,
                          )
                        }
                      />
                    )
                  }
                  label={
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-[13px] font-medium text-gray-600">
                        {team.name}
                      </span>
                      {/* A team the ORG CHART lists renders even with nothing in
                          it: "we are asking for nobody here" is a real answer,
                          and an empty team still has to be somewhere to drop a
                          role. A name only the data knows is marked, so a stale
                          spelling is visible rather than passing as structure. */}
                      {!team.fromChart && (
                        <span
                          className="shrink-0 rounded-sm border border-gray-200 px-1 text-[10px] leading-4 text-gray-400"
                          title="Not in the org chart — added here or carried on a role"
                        >
                          not in the chart
                        </span>
                      )}
                    </span>
                  }
                />
                {tOpen && (
                  <>
                    {/* Directly under its own header, not at the foot of the
                        team: the seats that exist are the context for the seats
                        being asked for, and context after the fact is a scroll
                        back up. Collapsed, so it stays context. */}
                    <TeamRoster
                      members={team.members}
                      tracks={tracks}
                      depth={3}
                      label={`Current team · ${team.name}`}
                      busy={busy}
                      backfills={backfills}
                      onRequestBackfill={(m) => onRequestBackfill(unit.name, m)}
                      restructuring={restructuring}
                      unitChoices={unitChoices}
                      deptName={deptName}
                      unitName={unit.name}
                      onRestructure={onRestructure}
                    />
                    {roleRows(team.roles, 3)}
                    {composing === team.key ? (
                      <ComposerRow
                        tracks={tracks}
                        depth={3}
                        saving={anyBusy}
                        error={composeError}
                        onCancel={() => setComposing(null)}
                        onPlace={(month, f) => onPlace(unit.name, team.name, month, f)}
                      />
                    ) : (
                      <AddRoleRow
                        tracks={tracks}
                        depth={3}
                        label="Add role"
                        onClick={() => setComposing(team.key)}
                      />
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/*
            Loose roles sit directly ABOVE the button that creates them. A role
            added at unit level has no team bucket, so this is where it lands —
            and "added exactly where you clicked" is the whole point: it used to
            appear at the top of the unit, out of view, which read as vanishing.

            It is also where the job-family map gave up. That used to be an
            unexplained gap between the teams and the button; now it is a labelled
            group and a drop target, so "these need a team" is the visible ask
            rather than something a lead has to infer.
          */}
          {movable && unit.looseRoles.length > 0 && (
            <NoTeamRow
              tracks={tracks}
              count={unit.looseRoles.length}
              drop={teamMove.dropProps(unit.key, '')}
            />
          )}
          {unit.looseRoles.length > 0 && roleRows(unit.looseRoles, 2)}

          {/* History, not work: approved then dropped, greyed and shut by default. */}
          <CancelledRoles roles={unit.cancelled ?? []} depth={2} />

          {composing === unit.key ? (
            <ComposerRow
              tracks={tracks}
              depth={2}
              saving={anyBusy}
              error={composeError}
              onCancel={() => setComposing(null)}
              onPlace={(month, f) => onPlace(unit.name, '', month, f)}
            />
          ) : (
            <AddRoleRow
              tracks={tracks}
              depth={2}
              /*
                One button when the unit IS its own team. Talent Acquisition has a
                single team of the same name, so the tree collapses the pair
                (`collapseSoleTeam`) and this reads "Add role" — there used to be
                an "Add role" and an "Add role directly to Talent Acquisition"
                one row apart, doing the same thing.
              */
              label={unit.teams.length ? `Add role directly to ${unit.name}` : 'Add role'}
              onClick={() => setComposing(unit.key)}
            />
          )}

        </>
      )}
    </>
  );
}

/**
 * "No team yet" — the roles the job-family map could not place.
 *
 * Also the drop target that CLEARS a team, which is the only way to undo a wrong
 * map entry: without it a lead could move a role somewhere else wrong but never
 * back out.
 */
function NoTeamRow({
  tracks,
  count,
  drop,
}: {
  tracks: Track[];
  count: number;
  drop: { active: boolean; handlers: React.HTMLAttributes<HTMLElement> };
}) {
  return (
    <div
      {...drop.handlers}
      className={`grid min-h-[30px] items-stretch border-b border-gray-100 ${
        drop.active ? 'outline outline-2 -outline-offset-2 outline-blue-500 bg-blue-100' : ''
      }`}
      style={{ gridTemplateColumns: 'var(--demand-tmpl)' }}
    >
      <div
        className="sticky left-0 z-[2] flex min-w-0 items-center border-r border-gray-200 bg-white pr-2"
        style={{ paddingLeft: indentPx(2) }}
      >
        <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
          No team yet
          <span className="ml-1.5 tabular-nums">{count}</span>
        </span>
      </div>
      {tracks.map((t, i) => (
        <div key={i} className={zoneClass(t)} />
      ))}
    </div>
  );
}
